import { TextField } from "@mui/material";
import { StandardTextFieldProps } from "@mui/material/TextField";
import { MASK_NUMERIC_CHAR } from "components/MprcTextFieldMask";
import { useMprcTheme } from "components/MprcThemeProvider";
import LibUtils from "lib/LibUtils";
import { useSession } from "providers/SessionProvider";
import React, { useState } from "react";
import { NumericFormat, PatternFormat } from "react-number-format";
import { CurrencyFormatInput } from "./CurrencyFormatInput";

////////////////////////////////////////////////////////////////////////////

interface IViewError {
    error: boolean;
    message?: string;
}

interface INumberMaskProps {
    mask?: string;
    precision?: number;
    isCurrency?: boolean;
    min?: number;
    max?: number;
    errorMessage?: string;
    onChange: (value: string, formattedValue: string) => void;
}

interface IProps extends Omit<StandardTextFieldProps, "InputProps" | "onChange">, INumberMaskProps { }

////////////////////////////////////////////////////////////////////////////

export const MprcTextFieldNumberMask: React.FC<IProps> = (props) => {
    ////////////////////////////////////////////////////////////////////////////
    const { min, max, mask, precision, onChange, error, helperText, isCurrency, errorMessage, ...rest } = props;
    const { config } = useSession();
    const { theme } = useMprcTheme();
    const session = useSession();

    const [valueError, setValueError] = useState<IViewError>({ error: false, message: undefined });

    ////////////////////////////////////////////////////////////////////////////

    ///
    const formatNumericValue = (value: string) => {
        return Intl.NumberFormat(session.config.defaultLocale, { minimumFractionDigits: precision ?? 0 }).format(Number(value));
    };

    ///
    const validateValue = (value: string) => {
        let message = theme.MprcTextFieldNumberMask.invalidValueText;
        let error = false;

        if (value) {
            let floatValue = parseFloat(value);
            if (min && floatValue < min) {
                message += theme.MprcTextFieldNumberMask.minValueText + ` ${min}. `;
                error = true;
            }

            if (max && floatValue > max) {
                message += theme.MprcTextFieldNumberMask.maxValueText + ` ${max}. `;
                error = true;
            }
        }

        setValueError({ error, message: error ? errorMessage ?? message : undefined });
    };

    const muiTextFieldProps = {
        ...rest,
        error: valueError.error || error,
        helperText: valueError.error === true ? valueError.message : helperText,
        defaultValue: rest.defaultValue as any,
        value: rest.value as any,
        type: rest.type as any,
    };

    if (mask != undefined) {
        return <PatternFormat
            patternChar={MASK_NUMERIC_CHAR}
            {...muiTextFieldProps}
            format={mask}
            mask={"_"}
            onValueChange={(values) => {
                onChange(values.value, values.formattedValue);
            }}
            customInput={TextField}

        />;
    }
    else {

        const numberSeparators = LibUtils.getLocaleNumberSeparators(config.defaultLocale);
        if (isCurrency === true) {

            return <TextField
                {...muiTextFieldProps}
                onChange={(e) => {
                    const value = e.target.value;
                    if (mask == undefined) validateValue(value);
                    onChange(value, formatNumericValue(value));
                }}
                slotProps={{
                    input: {
                        inputComponent: CurrencyFormatInput as any,
                        inputProps: { locale: config.defaultLocale, currentValue: props.value }
                    }
                }}
            />;

        } else {

            const isDecimalNumber = precision != undefined && precision > 0;
            return <NumericFormat
                onValueChange={(values) => {
                    validateValue(values.value);
                    onChange(values.value, values.formattedValue);
                }}
                {...muiTextFieldProps}
                customInput={TextField}
                decimalSeparator={isDecimalNumber ? numberSeparators.decimalSeparator : undefined}
                decimalScale={precision}
            />;
        }
    }
};
